/**
 * BookshopAdmin — Faith Works Bookshop admin portal (Phase 1 / Iter 419).
 *
 * Tabs: Dashboard · Products · Categories · Orders · Inventory · Embed.
 * Uses REACT_APP_BACKEND_URL/api/bookshop/admin/... with the current
 * admin JWT (via lib/api). Not customer-facing — protected by the
 * standard admin route guard already applied at App level.
 */
import React, { useState, useEffect } from 'react';
import { CURRENCIES as ALL_CURRENCIES } from '@/lib/currencies';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { PendingPayments } from './bookshop/PendingPayments';
import { Uploads } from './bookshop/Uploads';
import { CollectionQRScan } from './bookshop/CollectionQRScan';
import { UploadPicker } from '@/components/bookshop/UploadPicker';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { toast } from 'sonner';
import { Loader2, Plus, Edit2, Trash2, Copy, ExternalLink, Package, ShoppingBag, Layers, DollarSign, AlertTriangle, Eye, EyeOff, Image as ImageIcon, FileText, Upload, ScanLine, Minus, TrendingUp, Undo2, Search } from 'lucide-react';
import { MediaUpload } from '@/components/MediaUpload';

const CURRENCIES = ALL_CURRENCIES.map((c) => c.code);
const FORMATS = ['Paperback', 'Hardback', 'PDF', 'EPUB', 'MP3', 'MP4', 'Bundle'];
const PRODUCT_TYPES = ['book', 'ebook', 'study_manual', 'bible', 'devotional', 'audio', 'video', 'merch', 'conference'];

const emptyProduct = () => ({
  title: '', subtitle: '', author: '', publisher: '', sku: '', isbn: '',
  description: '', short_description: '',
  category_id: '', product_type: 'book', format: 'Paperback', language: 'English',
  price: 0, sale_price: null, rent_price: null, currency: 'GBP',
  stock: 0, low_stock_level: 3,
  cover_image_url: '', preview_video_url: '', sample_chapter_url: '',
  asset_url: '',
  is_featured: false, is_bestseller: false, is_new_release: false, is_digital: false,
  status: 'draft', tags: [],
});

const BookshopAdmin = () => {
  const [tab, setTab] = useState('dashboard');
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4" data-testid="bookshop-admin">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingBag className="h-6 w-6 text-indigo-600" /> Faith Works Bookshop Admin
          </h1>
          <p className="text-sm text-slate-500">
            Backend for <a className="text-indigo-600 underline" href="https://faithworldministries.com/faith-works-bookshop/index.html" target="_blank" rel="noreferrer">the public storefront <ExternalLink className="h-3 w-3 inline" /></a>
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 sm:grid-cols-4 h-auto gap-1 md:inline-flex md:grid-cols-none">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="pending-payments" data-testid="tab-pending-payments">Pending Payments</TabsTrigger>
          <TabsTrigger value="collection-qr" data-testid="tab-collection-qr">Collection QR</TabsTrigger>
          <TabsTrigger value="uploads" data-testid="tab-uploads">Uploads</TabsTrigger>
          <TabsTrigger value="pos" data-testid="tab-pos">POS</TabsTrigger>
          <TabsTrigger value="inventory" data-testid="tab-inventory">Inventory</TabsTrigger>
          <TabsTrigger value="authors" data-testid="tab-authors">Authors</TabsTrigger>
          <TabsTrigger value="publishers" data-testid="tab-publishers">Publishers</TabsTrigger>
          <TabsTrigger value="discounts" data-testid="tab-discounts">Discounts</TabsTrigger>
          <TabsTrigger value="content" data-testid="tab-content">Content</TabsTrigger>
          <TabsTrigger value="media" data-testid="tab-media">Media</TabsTrigger>
          <TabsTrigger value="preview" data-testid="tab-preview">Preview</TabsTrigger>
          <TabsTrigger value="embed" data-testid="tab-embed">Embed</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-4"><Dashboard /></TabsContent>
        <TabsContent value="products" className="mt-4"><Products /></TabsContent>
        <TabsContent value="categories" className="mt-4"><Categories /></TabsContent>
        <TabsContent value="orders" className="mt-4"><Orders /></TabsContent>
        <TabsContent value="pending-payments" className="mt-4"><PendingPayments /></TabsContent>
        <TabsContent value="collection-qr" className="mt-4"><CollectionQRScan /></TabsContent>
        <TabsContent value="uploads" className="mt-4"><Uploads /></TabsContent>
        <TabsContent value="pos" className="mt-4"><POSTerminal /></TabsContent>
        <TabsContent value="inventory" className="mt-4"><Inventory /></TabsContent>
        <TabsContent value="authors" className="mt-4"><Authors /></TabsContent>
        <TabsContent value="publishers" className="mt-4"><Publishers /></TabsContent>
        <TabsContent value="discounts" className="mt-4"><Discounts /></TabsContent>
        <TabsContent value="content" className="mt-4"><ContentBlocks /></TabsContent>
        <TabsContent value="media" className="mt-4"><MediaLibrary /></TabsContent>
        <TabsContent value="preview" className="mt-4"><PreviewTokens /></TabsContent>
        <TabsContent value="embed" className="mt-4"><EmbedInstructions /></TabsContent>
      </Tabs>
    </div>
  );
};

// ================== DASHBOARD ==================
const StatCard = ({ icon: Icon, label, value, hint, color = 'text-indigo-600' }) => (
  <Card><CardContent className="p-5">
    <div className="flex items-start justify-between"><Icon className={`h-6 w-6 ${color}`} />
      <div className="text-right"><p className="text-2xl font-bold">{value}</p><p className="text-xs text-slate-500">{label}</p></div>
    </div>
    {hint && <p className="text-[11px] text-slate-400 mt-2">{hint}</p>}
  </CardContent></Card>
);

const Dashboard = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['bookshop-dashboard'],
    queryFn: async () => (await api.get('/bookshop/admin/dashboard')).data,
  });
  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;
  const s = data || {};
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <StatCard icon={Package} label="Published products" value={s.products?.published || 0} hint={`of ${s.products?.total || 0} total`} />
      <StatCard icon={AlertTriangle} label="Low / out of stock" value={s.products?.low_stock || 0} hint={`${s.products?.out_of_stock || 0} out of stock`} color="text-amber-600" />
      <StatCard icon={ShoppingBag} label="Pending orders" value={s.orders?.pending || 0} hint={`${s.orders?.paid || 0} paid`} color="text-emerald-600" />
      <StatCard icon={Layers} label="Customers" value={s.customers || 0} />
      <Card className="col-span-2 lg:col-span-4"><CardHeader><CardTitle className="text-base">Revenue by currency (paid orders)</CardTitle></CardHeader>
        <CardContent>
          {(s.revenue_by_currency || []).length === 0 ? (
            <p className="text-sm text-slate-400">No paid orders yet.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {(s.revenue_by_currency || []).map((r) => (
                <div key={r.currency} className="border rounded p-3">
                  <p className="text-xs text-slate-500">{r.currency}</p>
                  <p className="text-lg font-semibold">{Number(r.total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  <p className="text-xs text-slate-400">{r.count} orders</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <div className="col-span-2 lg:col-span-4">
        <PaymentTrafficLight />
      </div>
    </div>
  );
};


const HEALTH_STYLES = {
  healthy:  { label: 'Healthy',  cls: 'border-emerald-500 text-emerald-700 bg-emerald-50', dot: 'bg-emerald-500' },
  degraded: { label: 'Degraded', cls: 'border-amber-500 text-amber-700 bg-amber-50', dot: 'bg-amber-500' },
  critical: { label: 'Critical', cls: 'border-rose-500 text-rose-700 bg-rose-50', dot: 'bg-rose-500' },
  idle:     { label: 'Idle',     cls: 'border-slate-300 text-slate-500 bg-slate-50', dot: 'bg-slate-400' },
};

const PROVIDER_LABELS = {
  stripe: 'Card (Stripe)', paypal: 'PayPal', bank_transfer: 'Bank transfer',
  ecocash: 'EcoCash', cash_on_collection: 'Cash on collection',
};

function PaymentTrafficLight() {
  const [days, setDays] = React.useState(7);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['bookshop-payments-traffic', days],
    queryFn: async () => (await api.get(`/bookshop/admin/payments/traffic-light?days=${days}`)).data,
  });
  if (isLoading) return <Card><CardContent className="p-4 text-sm text-slate-500">Loading payment health…</CardContent></Card>;
  const rows = data?.providers || [];
  const t = data?.totals || {};
  return (
    <Card data-testid="payment-traffic-light-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Payment health · last {days} days</CardTitle>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
            <SelectTrigger className="h-8 w-28" data-testid="ptl-days">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 7, 30, 90].map(d => (
                <SelectItem key={d} value={String(d)}>{d} day{d > 1 ? 's' : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => refetch()}
                     data-testid="ptl-refresh">Refresh</Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {rows.map(p => {
            const st = HEALTH_STYLES[p.health] || HEALTH_STYLES.idle;
            const rate = p.success_rate === null ? '—' : `${Math.round(p.success_rate * 100)}%`;
            return (
              <div key={p.method} className={`border-2 rounded-lg p-3 ${st.cls}`}
                     data-testid={`ptl-row-${p.method}`}>
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${st.dot}`} />
                  <div className="text-xs font-medium text-slate-700 flex-1 truncate">
                    {PROVIDER_LABELS[p.method] || p.method}
                  </div>
                </div>
                <div className="text-2xl font-semibold mt-1" data-testid={`ptl-rate-${p.method}`}>
                  {rate}
                </div>
                <div className="text-[10px] text-slate-600 mt-1">
                  {p.paid} paid · {p.failed || p.rejected || 0} failed
                  {p.awaiting_review > 0 && <> · <strong>{p.awaiting_review} to review</strong></>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex items-center gap-4 text-xs text-slate-500 flex-wrap">
          <span><strong className="text-emerald-700">{t.paid || 0}</strong> paid</span>
          <span><strong className="text-rose-700">{t.rejected || 0}</strong> rejected</span>
          <span><strong className="text-amber-700">{t.awaiting_review || 0}</strong> awaiting review</span>
          <span>Total: {t.total || 0}</span>
          {t.success_rate !== null && t.success_rate !== undefined && (
            <span>Overall rate: <strong>{Math.round(t.success_rate * 100)}%</strong></span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ================== PRODUCTS ==================
const Products = () => {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [editing, setEditing] = useState(null);   // null=closed, {}=new, {id..}=edit

  const { data, isLoading } = useQuery({
    queryKey: ['bookshop-products', q, status],
    queryFn: async () => (await api.get('/bookshop/admin/products', { params: { q: q || undefined, status: status || undefined, limit: 200 } })).data,
  });
  const { data: cats = [] } = useQuery({
    queryKey: ['bookshop-cats'],
    queryFn: async () => (await api.get('/bookshop/admin/categories')).data,
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/bookshop/admin/products/${id}`),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['bookshop-products'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const togglePub = useMutation({
    mutationFn: (id) => api.post(`/bookshop/admin/products/${id}/publish`),
    onSuccess: (r) => { toast.success(`Now ${r.data.status}`); qc.invalidateQueries({ queryKey: ['bookshop-products'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Input placeholder="Search title / SKU / ISBN / author" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="product-search" />
        <Select value={status || 'all'} onValueChange={(v) => setStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40" data-testid="product-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Button className="ml-auto" onClick={() => setEditing(emptyProduct())} data-testid="new-product-btn"><Plus className="h-4 w-4 mr-1" /> New product</Button>
      </div>

      {isLoading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : (
        (data?.products || []).length === 0 ? (
          <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-400">
            No products yet. Click <b>New product</b> to add the first Faith Works Bookshop title.
          </CardContent></Card>
        ) : (
          <Card><CardContent className="p-0"><Table>
            <TableHeader><TableRow>
              <TableHead>Title</TableHead><TableHead>Author</TableHead>
              <TableHead>SKU</TableHead><TableHead>Price</TableHead>
              <TableHead>Stock</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(data?.products || []).map((p) => (
                <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                  <TableCell className="font-medium">
                    {p.title}
                    <div className="flex gap-1 mt-1">
                      {p.is_featured && <Badge variant="outline" className="text-[10px]">Featured</Badge>}
                      {p.is_bestseller && <Badge variant="outline" className="text-[10px]">Bestseller</Badge>}
                      {p.is_new_release && <Badge variant="outline" className="text-[10px]">New</Badge>}
                      {p.is_digital && <Badge variant="outline" className="text-[10px]">Digital</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{p.author || '—'}</TableCell>
                  <TableCell className="text-xs font-mono">{p.sku}</TableCell>
                  <TableCell>{p.currency} {Number(p.sale_price || p.price).toFixed(2)}</TableCell>
                  <TableCell className={p.stock <= (p.low_stock_level || 3) ? 'text-amber-600 font-medium' : ''}>{p.stock}</TableCell>
                  <TableCell><Badge className={p.status === 'published' ? 'bg-emerald-100 text-emerald-700' : p.status === 'archived' ? 'bg-slate-100 text-slate-500' : 'bg-amber-100 text-amber-700'}>{p.status}</Badge></TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(p)} data-testid={`edit-product-${p.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => togglePub.mutate(p.id)} data-testid={`toggle-publish-${p.id}`}>{p.status === 'published' ? 'Unpublish' : 'Publish'}</Button>
                    <Button size="sm" variant="ghost" onClick={() => { if (window.confirm(`Delete ${p.title}?`)) del.mutate(p.id); }} data-testid={`del-product-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></CardContent></Card>
        )
      )}

      <ProductEditor
        open={!!editing}
        initial={editing}
        categories={cats}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['bookshop-products'] }); }}
      />
    </div>
  );
};

const ProductEditor = ({ open, initial, categories, onClose, onSaved }) => {
  const [form, setForm] = useState(emptyProduct());
  React.useEffect(() => { if (open && initial) setForm({ ...emptyProduct(), ...initial }); }, [open, initial]);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price) || 0, stock: Number(form.stock) || 0, low_stock_level: Number(form.low_stock_level) || 3, sale_price: form.sale_price === '' || form.sale_price === null ? null : Number(form.sale_price) };
      if (isEdit) await api.put(`/bookshop/admin/products/${initial.id}`, payload);
      else await api.post('/bookshop/admin/products', payload);
      toast.success(isEdit ? 'Product updated' : 'Product created');
      onSaved?.();
    } catch (e) { toast.error(getErrorMessage(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-editor">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit product' : 'New product'}</DialogTitle>
          <DialogDescription>Fields marked * are required. Save as draft or publish immediately.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Title *</Label><Input value={form.title} onChange={(e) => set('title', e.target.value)} data-testid="product-title" /></div>
            <div><Label>Subtitle</Label><Input value={form.subtitle || ''} onChange={(e) => set('subtitle', e.target.value)} /></div>
            <div><Label>Author</Label><Input value={form.author || ''} onChange={(e) => set('author', e.target.value)} data-testid="product-author" /></div>
            <div><Label>Publisher</Label><Input value={form.publisher || ''} onChange={(e) => set('publisher', e.target.value)} /></div>
            <div><Label>SKU</Label><Input value={form.sku || ''} onChange={(e) => set('sku', e.target.value)} placeholder="Auto-generated" /></div>
            <div><Label>ISBN</Label><Input value={form.isbn || ''} onChange={(e) => set('isbn', e.target.value)} /></div>
          </div>
          <div><Label>Short description</Label><Textarea rows={2} value={form.short_description || ''} onChange={(e) => set('short_description', e.target.value)} /></div>
          <div><Label>Description</Label><Textarea rows={4} value={form.description || ''} onChange={(e) => set('description', e.target.value)} /></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><Label>Category</Label>
              <Select value={form.category_id || 'none'} onValueChange={(v) => set('category_id', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="product-category"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Type</Label>
              <Select value={form.product_type} onValueChange={(v) => set('product_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRODUCT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Format</Label>
              <Select value={form.format || 'Paperback'} onValueChange={(v) => set('format', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label>Price *</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => set('price', e.target.value)} data-testid="product-price" /></div>
            <div><Label>Sale price</Label><Input type="number" step="0.01" value={form.sale_price ?? ''} onChange={(e) => set('sale_price', e.target.value)} /></div>
            <div><Label>Rent price (7-day)</Label><Input type="number" step="0.01" value={form.rent_price ?? ''} onChange={(e) => set('rent_price', e.target.value === '' ? null : e.target.value)} placeholder="Leave empty to disable rentals" data-testid="product-rent-price" /></div>
            <div><Label>Currency</Label>
              <Select value={form.currency} onValueChange={(v) => set('currency', v)}>
                <SelectTrigger data-testid="product-currency"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => set('stock', e.target.value)} data-testid="product-stock" /></div>
          </div>
          <div>
            <div className="flex gap-2 items-start">
              <MediaUpload value={form.cover_image_url || ''} onChange={(url) => set('cover_image_url', url)} purpose="cover" label="Cover image" testId="product-cover-url" className="flex-1" previewClassName="h-20" />
              <UploadPicker
                kind="images"
                value={form.cover_image_url}
                onSelect={(url) => set('cover_image_url', url)}
                triggerTestId="product-cover-picker"
                label="Library"
              />
            </div>
          </div>
          <div>
            <Label>Digital asset URL (for e-books / audio / video rentals)</Label>
            <div className="flex gap-2 items-center">
              <Input value={form.asset_url || ''}
                       onChange={(e) => set('asset_url', e.target.value)}
                       placeholder="https://… or pick from library"
                       data-testid="product-asset-url" />
              <UploadPicker
                kind="books"
                filterKinds={['books', 'audio', 'video', 'private']}
                value={form.asset_url}
                onSelect={(url) => set('asset_url', url)}
                triggerTestId="product-asset-picker"
                label="Library"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            {['is_featured', 'is_bestseller', 'is_new_release', 'is_digital'].map((k) => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <Switch checked={!!form[k]} onCheckedChange={(v) => set(k, v)} data-testid={`toggle-${k}`} />
                {k.replace('is_', '').replace('_', ' ')}
              </label>
            ))}
          </div>
          <div><Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => set('status', v)}>
              <SelectTrigger className="w-40" data-testid="product-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-product-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} {isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ================== CATEGORIES ==================
const Categories = () => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data = [], isLoading } = useQuery({
    queryKey: ['bookshop-cats-admin'],
    queryFn: async () => (await api.get('/bookshop/admin/categories')).data,
  });
  const [form, setForm] = useState({ name: '', slug: '', description: '', order: 0 });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/bookshop/admin/categories/${id}`),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['bookshop-cats-admin'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const save = async () => {
    if (!form.name.trim()) return toast.error('Name required');
    try {
      if (editing?.id) await api.put(`/bookshop/admin/categories/${editing.id}`, form);
      else await api.post('/bookshop/admin/categories', form);
      toast.success('Saved');
      setEditing(null);
      setForm({ name: '', slug: '', description: '', order: 0 });
      qc.invalidateQueries({ queryKey: ['bookshop-cats-admin'] });
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <Card><CardHeader className="flex-row items-center justify-between">
      <CardTitle className="text-base">Categories</CardTitle>
      <Button size="sm" onClick={() => { setEditing({ id: null }); setForm({ name: '', slug: '', description: '', order: 0 }); }} data-testid="new-category-btn"><Plus className="h-4 w-4 mr-1" /> New</Button>
    </CardHeader><CardContent>
      {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
        <div className="space-y-2">
          {data.map((c) => (
            <div key={c.id} className="flex items-center justify-between border rounded p-2" data-testid={`category-row-${c.id}`}>
              <div><p className="font-medium">{c.name}</p><p className="text-xs text-slate-500">/{c.slug} · {c.product_count || 0} products</p></div>
              <div className="space-x-1">
                <Button size="sm" variant="ghost" onClick={() => { setEditing(c); setForm(c); }}><Edit2 className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => { if (window.confirm(`Delete ${c.name}?`)) del.mutate(c.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
          {data.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No categories yet.</p>}
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="category-editor">
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit' : 'New'} category</DialogTitle>
            <DialogDescription>Categories group products on the storefront filters.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="category-name" /></div>
            <div><Label>Slug</Label><Input value={form.slug || ''} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto" /></div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Order</Label><Input type="number" value={form.order || 0} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button onClick={save} data-testid="save-category-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
};

// ================== ORDERS ==================
const Orders = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['bookshop-orders-admin'],
    queryFn: async () => (await api.get('/bookshop/admin/orders')).data,
  });
  const updateStatus = useMutation({
    mutationFn: ({ id, status }) => api.put(`/bookshop/admin/orders/${id}/status`, { status }),
    onSuccess: () => { toast.success('Status updated'); qc.invalidateQueries({ queryKey: ['bookshop-orders-admin'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const STATUSES = ['awaiting_payment', 'paid', 'processing', 'ready_for_collection', 'dispatched', 'delivered', 'completed', 'cancelled', 'refunded'];

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;
  const orders = data?.orders || [];
  if (orders.length === 0) return <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-400">No orders yet.</CardContent></Card>;
  return (
    <Card><CardContent className="p-0"><Table>
      <TableHeader><TableRow><TableHead>Order #</TableHead><TableHead>Customer</TableHead><TableHead>Total</TableHead><TableHead>Payment</TableHead><TableHead>Status</TableHead><TableHead>Placed</TableHead></TableRow></TableHeader>
      <TableBody>
        {orders.map((o) => (
          <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
            <TableCell className="font-mono text-xs">{o.order_no}</TableCell>
            <TableCell className="text-sm"><div>{o.customer?.name}</div><div className="text-xs text-slate-500">{o.customer?.email}</div></TableCell>
            <TableCell>{o.currency} {Number(o.total).toFixed(2)}</TableCell>
            <TableCell><Badge variant="outline" className="text-[10px]">{o.payment_method}</Badge> <Badge className={o.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700 text-[10px]' : 'bg-amber-100 text-amber-700 text-[10px]'}>{o.payment_status}</Badge></TableCell>
            <TableCell>
              <Select value={o.status} onValueChange={(v) => updateStatus.mutate({ id: o.id, status: v })}>
                <SelectTrigger className="w-44 h-8 text-xs" data-testid={`order-status-${o.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </TableCell>
            <TableCell className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString()}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table></CardContent></Card>
  );
};

// ================== POS TERMINAL ==================
const POSTerminal = () => {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [saleLines, setSaleLines] = useState([]); // [{product, qty, line_discount}]
  const [discount, setDiscount] = useState(0);
  const [taxRate, setTaxRate] = useState(0);
  const [payment, setPayment] = useState('cash');
  const [paymentRef, setPaymentRef] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [posting, setPosting] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const { data: results } = useQuery({
    queryKey: ['pos-search', debouncedQ],
    queryFn: async () => (await api.get('/bookshop/admin/products', { params: { q: debouncedQ || undefined, limit: 12, status: 'published' } })).data,
    enabled: debouncedQ.length > 0,
  });
  const { data: today } = useQuery({
    queryKey: ['pos-today'],
    queryFn: async () => (await api.get('/bookshop/admin/pos/today')).data,
  });

  const addProduct = (product) => {
    setSaleLines((prev) => {
      const idx = prev.findIndex((l) => l.product.id === product.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + 1 };
        return copy;
      }
      return [...prev, { product, qty: 1, line_discount: 0 }];
    });
    setQ('');
  };
  const updateLine = (id, patch) => setSaleLines((prev) => prev.map((l) => l.product.id === id ? { ...l, ...patch } : l));
  const removeLine = (id) => setSaleLines((prev) => prev.filter((l) => l.product.id !== id));

  const subtotal = React.useMemo(() => saleLines.reduce((s, l) => {
    const price = Number(l.product.sale_price || l.product.price || 0);
    const gross = price * l.qty;
    return s + Math.max(0, gross - Number(l.line_discount || 0));
  }, 0), [saleLines]);
  const orderDiscount = Math.min(subtotal, Math.max(0, Number(discount || 0)));
  const net = Math.max(0, subtotal - orderDiscount);
  const tax = net * Math.max(0, Number(taxRate || 0));
  const total = net + tax;
  const currency = saleLines[0]?.product?.currency || 'GBP';

  const completeSale = async () => {
    if (saleLines.length === 0) { toast.error('Add at least one item'); return; }
    setPosting(true);
    try {
      const payload = {
        lines: saleLines.map((l) => ({ product_id: l.product.id, quantity: l.qty, line_discount: Number(l.line_discount || 0) })),
        discount: Number(discount || 0),
        tax_rate: Number(taxRate || 0),
        payment_method: payment,
        payment_reference: paymentRef || undefined,
        customer_name: customerName || undefined,
        customer_email: customerEmail || undefined,
        notes: notes || undefined,
      };
      const { data } = await api.post('/bookshop/admin/pos/sales', payload);
      setLastReceipt(data.sale);
      setSaleLines([]); setDiscount(0); setTaxRate(0); setPaymentRef(''); setCustomerName(''); setCustomerEmail(''); setNotes('');
      toast.success(`Sale recorded: ${data.receipt_no}`);
      qc.invalidateQueries({ queryKey: ['pos-today'] });
      qc.invalidateQueries({ queryKey: ['bookshop-products'] });
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setPosting(false); }
  };

  const printReceipt = () => {
    if (!lastReceipt) return;
    const w = window.open('', '_blank', 'width=380,height=600');
    if (!w) return;
    const lines = (lastReceipt.lines || []).map((l) => `${l.product_title.padEnd(28)}  ${l.quantity} x ${l.unit_price.toFixed(2)}  ${l.line_total.toFixed(2)}`).join('\n');
    w.document.write(`<pre style="font:12px monospace;padding:12px;">
FAITH WORKS BOOKSHOP
Receipt: ${lastReceipt.receipt_no}
${new Date(lastReceipt.created_at).toLocaleString()}
Cashier: ${lastReceipt.cashier_email || ''}
${lastReceipt.customer_name ? 'Customer: ' + lastReceipt.customer_name + '\\n' : ''}
-----------------------------------------------------
${lines}
-----------------------------------------------------
Subtotal              ${(lastReceipt.subtotal || 0).toFixed(2)}
Discount              -${(lastReceipt.discount || 0).toFixed(2)}
Tax (${((lastReceipt.tax_rate || 0) * 100).toFixed(0)}%)               ${(lastReceipt.tax || 0).toFixed(2)}
TOTAL                 ${(lastReceipt.total || 0).toFixed(2)} ${(lastReceipt.lines[0]?.currency) || 'GBP'}

Paid via: ${lastReceipt.payment_method}
Thank you!
</pre><script>window.print();</script>`);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* LEFT: search + results + today */}
      <div className="lg:col-span-2 space-y-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><ScanLine className="h-4 w-4 text-indigo-600" /> Find product</CardTitle>
            <CardDescription>Type or scan title / SKU / ISBN / author.</CardDescription>
          </CardHeader>
          <CardContent>
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Scan or type..." data-testid="pos-search" />
            {debouncedQ && (
              <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                {(results?.products || []).map((p) => (
                  <button key={p.id} type="button" onClick={() => addProduct(p)} className="border rounded p-2 hover:border-indigo-500 text-left" data-testid={`pos-result-${p.id}`}>
                    <p className="text-sm font-medium truncate">{p.title}</p>
                    <p className="text-xs text-slate-500 truncate">{p.sku || p.isbn || '—'}</p>
                    <p className="text-xs mt-1">{(p.currency || 'GBP')} {Number(p.sale_price || p.price || 0).toFixed(2)} · stock {p.stock}</p>
                  </button>
                ))}
                {(results?.products || []).length === 0 && <p className="text-xs text-slate-400 col-span-full">No match.</p>}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-600" /> Today{`'`}s till</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
            <div><p className="text-xs text-slate-500">Sales</p><p className="text-xl font-semibold" data-testid="pos-today-count">{today?.sales_count || 0}</p></div>
            <div><p className="text-xs text-slate-500">Revenue</p><p className="text-xl font-semibold" data-testid="pos-today-gross">{(today?.gross_revenue || 0).toFixed(2)}</p></div>
            <div><p className="text-xs text-slate-500">Items sold</p><p className="text-xl font-semibold">{today?.items_sold || 0}</p></div>
          </CardContent>
        </Card>
        {lastReceipt && (
          <Card className="border-emerald-300">
            <CardHeader className="pb-2 flex-row justify-between items-center">
              <div><CardTitle className="text-sm text-emerald-700">Last receipt: {lastReceipt.receipt_no}</CardTitle>
                <CardDescription className="text-xs">Total {(lastReceipt.total || 0).toFixed(2)} {lastReceipt.lines[0]?.currency || ''}</CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={printReceipt} data-testid="pos-print-receipt">Print</Button>
            </CardHeader>
          </Card>
        )}
      </div>

      {/* RIGHT: current sale panel */}
      <div>
        <Card data-testid="pos-sale-panel">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><ShoppingBag className="h-4 w-4" /> Current sale</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {saleLines.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-6">Add a product to begin.</p>
            ) : (
              <div className="space-y-1">
                {saleLines.map((l) => {
                  const price = Number(l.product.sale_price || l.product.price || 0);
                  const lineTotal = Math.max(0, price * l.qty - Number(l.line_discount || 0));
                  return (
                    <div key={l.product.id} className="border rounded p-2 text-xs" data-testid={`pos-line-${l.product.id}`}>
                      <div className="flex justify-between gap-1">
                        <p className="font-medium truncate">{l.product.title}</p>
                        <button onClick={() => removeLine(l.product.id)} className="text-red-500" data-testid={`pos-remove-${l.product.id}`}><Trash2 className="h-3 w-3" /></button>
                      </div>
                      <p className="text-slate-500">{price.toFixed(2)} × {l.qty}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <Button size="sm" variant="outline" className="h-6 w-6 p-0" onClick={() => updateLine(l.product.id, { qty: Math.max(1, l.qty - 1) })} data-testid={`pos-qty-minus-${l.product.id}`}><Minus className="h-3 w-3" /></Button>
                        <span className="w-6 text-center">{l.qty}</span>
                        <Button size="sm" variant="outline" className="h-6 w-6 p-0" onClick={() => updateLine(l.product.id, { qty: l.qty + 1 })} data-testid={`pos-qty-plus-${l.product.id}`}><Plus className="h-3 w-3" /></Button>
                        <Input type="number" min="0" step="0.01" value={l.line_discount} onChange={(e) => updateLine(l.product.id, { line_discount: e.target.value })} className="h-6 text-xs w-20 ml-auto" placeholder="disc" data-testid={`pos-line-discount-${l.product.id}`} />
                        <span className="text-xs font-medium">{lineTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="border-t pt-2 space-y-1 text-xs">
              <div className="flex justify-between"><span>Subtotal</span><span data-testid="pos-subtotal">{subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between items-center">
                <span>Discount</span>
                <Input type="number" min="0" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} className="h-6 w-20 text-xs text-right" data-testid="pos-discount" />
              </div>
              <div className="flex justify-between items-center">
                <span>Tax rate</span>
                <Input type="number" min="0" step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} className="h-6 w-20 text-xs text-right" placeholder="0.20" data-testid="pos-tax-rate" />
              </div>
              <div className="flex justify-between"><span>Tax</span><span data-testid="pos-tax">{tax.toFixed(2)}</span></div>
              <div className="flex justify-between font-semibold text-base border-t pt-1"><span>Total</span><span data-testid="pos-total">{total.toFixed(2)} {currency}</span></div>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2">
              <div>
                <Label className="text-xs">Payment</Label>
                <select value={payment} onChange={(e) => setPayment(e.target.value)} className="w-full border rounded h-8 text-xs px-2" data-testid="pos-payment">
                  <option value="cash">Cash</option><option value="card">Card</option><option value="mobile">Mobile</option><option value="other">Other</option>
                </select>
              </div>
              <div><Label className="text-xs">Reference</Label><Input value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} className="h-8 text-xs" data-testid="pos-payment-ref" /></div>
              <div><Label className="text-xs">Customer name</Label><Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="h-8 text-xs" data-testid="pos-customer-name" /></div>
              <div><Label className="text-xs">Customer email</Label><Input value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} className="h-8 text-xs" data-testid="pos-customer-email" /></div>
            </div>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" rows={2} className="text-xs" data-testid="pos-notes" />
            <Button className="w-full" onClick={completeSale} disabled={posting || saleLines.length === 0} data-testid="pos-complete-sale">
              {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : `Complete sale · ${total.toFixed(2)} ${currency}`}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ================== INVENTORY ==================
const Inventory = () => {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [onlyLow, setOnlyLow] = useState(false);
  const [days, setDays] = useState(30);
  const [restockingId, setRestockingId] = useState(null);
  const [movementsForId, setMovementsForId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['inventory-summary', q, onlyLow, days],
    queryFn: async () => (await api.get('/bookshop/admin/inventory/summary', { params: { q: q || undefined, only_low_stock: onlyLow || undefined, days } })).data,
  });

  const restock = useMutation({
    mutationFn: ({ id, payload }) => api.post(`/bookshop/admin/products/${id}/restock`, payload),
    onSuccess: () => { toast.success('Restocked'); setRestockingId(null); qc.invalidateQueries({ queryKey: ['inventory-summary'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const rows = data?.products || [];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title / SKU / ISBN" className="max-w-sm" data-testid="inv-search" />
        <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={onlyLow} onChange={(e) => setOnlyLow(e.target.checked)} data-testid="inv-only-low" /> Only low stock</label>
        <div className="flex items-center gap-1 text-xs">
          <Label className="mb-0">Window (days)</Label>
          <Input type="number" min="1" max="365" value={days} onChange={(e) => setDays(Number(e.target.value) || 30)} className="w-20 h-7" data-testid="inv-days" />
        </div>
        <span className="ml-auto text-xs text-slate-500">{rows.length} items</span>
      </div>
      {isLoading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : (
        <div className="border rounded overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b"><tr>
              <th className="text-left p-2">Product</th>
              <th className="text-right p-2">Stock</th>
              <th className="text-right p-2">Low @</th>
              <th className="text-right p-2">Online</th>
              <th className="text-right p-2">POS</th>
              <th className="text-right p-2">Restocked</th>
              <th className="text-right p-2">Adj.</th>
              <th className="text-right p-2">Actions</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={8} className="p-6 text-center text-slate-400">No products match.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className={`border-b hover:bg-slate-50 ${r.is_low_stock ? 'bg-amber-50' : ''}`} data-testid={`inv-row-${r.id}`}>
                  <td className="p-2">
                    <p className="font-medium">{r.title}</p>
                    <p className="text-slate-400">{r.sku || r.isbn || '—'} · {r.author || ''}</p>
                  </td>
                  <td className={`p-2 text-right font-semibold ${r.is_low_stock ? 'text-amber-700' : ''}`} data-testid={`inv-stock-${r.id}`}>
                    {r.is_digital ? <Badge className="bg-slate-100 text-slate-500 text-[10px]">digital</Badge> : r.stock}
                    {r.is_low_stock && <AlertTriangle className="h-3 w-3 inline ml-1 text-amber-600" />}
                  </td>
                  <td className="p-2 text-right text-slate-500">{r.low_stock_level || '—'}</td>
                  <td className="p-2 text-right">{r.online_units}</td>
                  <td className="p-2 text-right">{r.pos_units}</td>
                  <td className="p-2 text-right text-emerald-700">{r.restocked_units}</td>
                  <td className={`p-2 text-right ${r.adjustment_delta < 0 ? 'text-red-600' : r.adjustment_delta > 0 ? 'text-emerald-700' : ''}`}>{r.adjustment_delta || '—'}</td>
                  <td className="p-2 text-right space-x-1">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setRestockingId(r.id)} disabled={r.is_digital} data-testid={`inv-restock-${r.id}`}>Restock</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setMovementsForId(r.id)} data-testid={`inv-movements-${r.id}`}>Log</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <RestockDialog open={!!restockingId} productId={restockingId} onClose={() => setRestockingId(null)} onSubmit={(payload) => restock.mutate({ id: restockingId, payload })} pending={restock.isPending} />
      <MovementsDialog open={!!movementsForId} productId={movementsForId} onClose={() => setMovementsForId(null)} days={days} />
    </div>
  );
};

const RestockDialog = ({ open, productId, onClose, onSubmit, pending }) => {
  const [qty, setQty] = useState(10);
  const [supplier, setSupplier] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [notes, setNotes] = useState('');
  useEffect(() => { if (!open) { setQty(10); setSupplier(''); setUnitCost(''); setNotes(''); } }, [open]);
  const submit = () => {
    if (Number(qty) <= 0) { toast.error('Quantity must be > 0'); return; }
    onSubmit({ quantity: Number(qty), supplier: supplier || undefined, unit_cost: unitCost ? Number(unitCost) : undefined, notes: notes || undefined });
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="restock-dialog">
        <DialogHeader><DialogTitle>Receive stock</DialogTitle><DialogDescription>Logs to inventory ledger as a restock movement.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Quantity</Label><Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="restock-qty" /></div>
          <div><Label>Unit cost (optional)</Label><Input type="number" step="0.01" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} data-testid="restock-unit-cost" /></div>
          <div className="col-span-2"><Label>Supplier</Label><Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="e.g. Wesley Owen Wholesale" data-testid="restock-supplier" /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="restock-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={pending} data-testid="restock-submit">{pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add stock'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MovementsDialog = ({ open, productId, onClose, days }) => {
  const { data } = useQuery({
    queryKey: ['inv-movements', productId, days],
    queryFn: async () => (await api.get('/bookshop/admin/inventory/movements', { params: { product_id: productId, limit: 200 } })).data,
    enabled: open && !!productId,
  });
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[70vh] overflow-y-auto" data-testid="movements-dialog">
        <DialogHeader><DialogTitle>Movements ledger</DialogTitle><DialogDescription>Every stock change on this product, newest first.</DialogDescription></DialogHeader>
        {(data?.movements || []).length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">No movements yet.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50"><tr>
              <th className="text-left p-1.5">When</th><th className="text-left p-1.5">Reason</th>
              <th className="text-left p-1.5">Channel</th><th className="text-right p-1.5">Δ</th>
              <th className="text-right p-1.5">New stock</th><th className="text-left p-1.5">Ref</th>
            </tr></thead>
            <tbody>
              {(data?.movements || []).map((m) => (
                <tr key={m.id} className="border-b" data-testid={`mov-row-${m.id}`}>
                  <td className="p-1.5">{new Date(m.at).toLocaleString()}</td>
                  <td className="p-1.5">{m.reason}</td>
                  <td className="p-1.5">{m.channel || '—'}</td>
                  <td className={`p-1.5 text-right font-medium ${m.delta < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{m.delta > 0 ? '+' : ''}{m.delta}</td>
                  <td className="p-1.5 text-right">{m.new_stock}</td>
                  <td className="p-1.5">{m.ref_no || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ================== AUTHORS ==================
const Authors = () => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['bookshop-authors'],
    queryFn: async () => (await api.get('/bookshop/admin/authors')).data,
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/bookshop/admin/authors/${id}`),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['bookshop-authors'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setOpen(true); };
  const authors = data?.authors || [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Authors</h2>
        <Button size="sm" onClick={openNew} data-testid="new-author-btn"><Plus className="h-4 w-4 mr-1" /> New author</Button>
      </div>
      {isLoading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : (
        authors.length === 0 ? (
          <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-400">No authors yet.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {authors.map((a) => (
              <Card key={a.id} data-testid={`author-row-${a.id}`}>
                <CardContent className="p-3 flex gap-3">
                  {a.photo_url && <img src={a.photo_url} alt={a.name} className="w-14 h-14 rounded-full object-cover" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 flex-wrap">
                      <p className="font-medium truncate">{a.name}</p>
                      {a.is_featured && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Featured</Badge>}
                    </div>
                    <p className="text-xs text-slate-500 truncate">/{a.slug}</p>
                    {a.bio && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{a.bio}</p>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(a)} data-testid={`edit-author-${a.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { if (window.confirm(`Delete ${a.name}?`)) del.mutate(a.id); }} data-testid={`del-author-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}
      <AuthorEditorDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={() => { setOpen(false); qc.invalidateQueries({ queryKey: ['bookshop-authors'] }); }} />
    </div>
  );
};

const AuthorEditorDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [form, setForm] = useState({ name: '', bio: '', photo_url: '', website_url: '', is_featured: false });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(initial ? {
      name: initial.name || '', bio: initial.bio || '',
      photo_url: initial.photo_url || '', website_url: initial.website_url || '',
      is_featured: !!initial.is_featured,
    } : { name: '', bio: '', photo_url: '', website_url: '', is_featured: false });
  }, [initial, open]);
  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/bookshop/admin/authors/${initial.id}`, form);
      else await api.post('/bookshop/admin/authors', form);
      toast.success(initial ? 'Updated' : 'Author added');
      onSaved?.();
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="author-editor">
        <DialogHeader><DialogTitle>{initial ? 'Edit author' : 'New author'}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="author-name" /></div>
          <MediaUpload value={form.photo_url} onChange={(url) => setForm({ ...form, photo_url: url })} purpose="avatar" label="Photo" testId="author-photo" previewClassName="h-16" />
          <div><Label>Website URL</Label><Input value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} data-testid="author-website" /></div>
          <div><Label>Bio</Label><Textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="author-bio" /></div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} data-testid="author-featured" /> Featured author</label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="author-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ================== PUBLISHERS ==================
const Publishers = () => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['bookshop-publishers'],
    queryFn: async () => (await api.get('/bookshop/admin/publishers')).data,
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/bookshop/admin/publishers/${id}`),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['bookshop-publishers'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const publishers = data?.publishers || [];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Publishers</h2>
        <Button size="sm" onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-publisher-btn"><Plus className="h-4 w-4 mr-1" /> New publisher</Button>
      </div>
      {isLoading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : (
        publishers.length === 0 ? (
          <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-400">No publishers yet.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {publishers.map((p) => (
              <Card key={p.id} data-testid={`publisher-row-${p.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{p.name}</p>
                      <p className="text-xs text-slate-500">{p.contact_email || p.website_url || '—'}</p>
                      {p.description && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{p.description}</p>}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => { setEditing(p); setOpen(true); }} data-testid={`edit-publisher-${p.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { if (window.confirm(`Delete ${p.name}?`)) del.mutate(p.id); }} data-testid={`del-publisher-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}
      <PublisherEditorDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={() => { setOpen(false); qc.invalidateQueries({ queryKey: ['bookshop-publishers'] }); }} />
    </div>
  );
};

const PublisherEditorDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [form, setForm] = useState({ name: '', description: '', website_url: '', contact_email: '', contact_phone: '', country: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(initial ? {
      name: initial.name || '', description: initial.description || '',
      website_url: initial.website_url || '', contact_email: initial.contact_email || '',
      contact_phone: initial.contact_phone || '', country: initial.country || '',
    } : { name: '', description: '', website_url: '', contact_email: '', contact_phone: '', country: '' });
  }, [initial, open]);
  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/bookshop/admin/publishers/${initial.id}`, form);
      else await api.post('/bookshop/admin/publishers', form);
      toast.success(initial ? 'Updated' : 'Publisher added');
      onSaved?.();
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="publisher-editor">
        <DialogHeader><DialogTitle>{initial ? 'Edit publisher' : 'New publisher'}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="publisher-name" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Website</Label><Input value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} data-testid="publisher-website" /></div>
            <div><Label>Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="publisher-country" /></div>
            <div><Label>Contact email</Label><Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="publisher-email" /></div>
            <div><Label>Contact phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="publisher-phone" /></div>
          </div>
          <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="publisher-description" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="publisher-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ================== DISCOUNTS ==================
const Discounts = () => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['bookshop-discounts'],
    queryFn: async () => (await api.get('/bookshop/admin/discounts')).data,
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/bookshop/admin/discounts/${id}`),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['bookshop-discounts'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const rows = data?.discounts || [];
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Discount codes</h2>
        <Button size="sm" onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-discount-btn"><Plus className="h-4 w-4 mr-1" /> New code</Button>
      </div>
      {isLoading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : (
        rows.length === 0 ? (
          <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-400">No discount codes yet.</CardContent></Card>
        ) : (
          <div className="border rounded overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b"><tr>
                <th className="text-left p-2">Code</th><th className="text-left p-2">Type</th>
                <th className="text-right p-2">Value</th><th className="text-right p-2">Min order</th>
                <th className="text-right p-2">Uses</th><th className="text-left p-2">Expires</th>
                <th className="text-left p-2">Status</th><th className="text-right p-2">Actions</th>
              </tr></thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id} className="border-b" data-testid={`discount-row-${d.id}`}>
                    <td className="p-2 font-mono">{d.code}</td>
                    <td className="p-2">{d.type}</td>
                    <td className="p-2 text-right">{d.type === 'percent' ? `${d.value}%` : Number(d.value).toFixed(2)}</td>
                    <td className="p-2 text-right">{Number(d.min_order || 0).toFixed(2)}</td>
                    <td className="p-2 text-right">{d.uses || 0}{d.max_uses ? `/${d.max_uses}` : ''}</td>
                    <td className="p-2">{fmtDate(d.expires_at)}</td>
                    <td className="p-2">{d.active ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Active</Badge> : <Badge className="bg-slate-200 text-slate-500 text-[10px]">Off</Badge>}</td>
                    <td className="p-2 text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => { setEditing(d); setOpen(true); }} data-testid={`edit-discount-${d.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { if (window.confirm(`Delete ${d.code}?`)) del.mutate(d.id); }} data-testid={`del-discount-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
      <DiscountEditorDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={() => { setOpen(false); qc.invalidateQueries({ queryKey: ['bookshop-discounts'] }); }} />
    </div>
  );
};

const DiscountEditorDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [form, setForm] = useState({ code: '', type: 'percent', value: 10, min_order: 0, max_uses: '', expires_at: '', active: true, notes: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(initial ? {
      code: initial.code || '', type: initial.type || 'percent',
      value: initial.value || 10, min_order: initial.min_order || 0,
      max_uses: initial.max_uses || '', expires_at: initial.expires_at ? String(initial.expires_at).slice(0, 10) : '',
      active: initial.active !== false, notes: initial.notes || '',
    } : { code: '', type: 'percent', value: 10, min_order: 0, max_uses: '', expires_at: '', active: true, notes: '' });
  }, [initial, open]);
  const submit = async () => {
    if (!form.code.trim()) { toast.error('Code required'); return; }
    const payload = {
      ...form, value: Number(form.value), min_order: Number(form.min_order || 0),
      max_uses: form.max_uses ? Number(form.max_uses) : null,
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
    };
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/bookshop/admin/discounts/${initial.id}`, payload);
      else await api.post('/bookshop/admin/discounts', payload);
      toast.success(initial ? 'Updated' : 'Code created');
      onSaved?.();
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="discount-editor">
        <DialogHeader><DialogTitle>{initial ? 'Edit discount' : 'New discount code'}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Code *</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="WELCOME10" data-testid="discount-code" /></div>
            <div><Label>Type</Label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="border rounded w-full h-9 px-2 text-sm" data-testid="discount-type">
                <option value="percent">Percent</option><option value="fixed">Fixed</option>
              </select>
            </div>
            <div><Label>Value {form.type === 'percent' ? '(%)' : ''}</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="discount-value" /></div>
            <div><Label>Min order</Label><Input type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} data-testid="discount-min" /></div>
            <div><Label>Max uses (blank = ∞)</Label><Input type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="discount-max" /></div>
            <div><Label>Expires on</Label><Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="discount-expires" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="discount-active" /> Active</label>
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="discount-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="discount-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ================== CONTENT BLOCKS (CMS) ==================
const SLOT_LABELS = {
  hero: 'Hero banner',
  announcement_bar: 'Announcement bar',
  about_intro: 'About intro',
  policy_shipping: 'Shipping policy (short)',
  policy_returns: 'Returns policy (short)',
  policy_privacy: 'Privacy policy (short)',
  footer_note: 'Footer note',
  // iter-425 full pages
  about_page: 'About page',
  contact_page: 'Contact page',
  returns_page: 'Returns page (full)',
  privacy_page: 'Privacy page (full)',
  terms_page: 'Terms & conditions',
  faq_page: 'FAQ page',
  shipping_page: 'Shipping information (full)',
  digital_resources_page: 'Digital resources page',
  conference_resources_page: 'Conference resources page',
  authors_intro: 'Authors page intro',
  newsletter_cta: 'Newsletter CTA',
};

const ContentBlocks = () => {
  const qc = useQueryClient();
  const [dirtyBySlot, setDirtyBySlot] = React.useState({});
  const [savingSlot, setSavingSlot] = useState('');
  const [pickerFor, setPickerFor] = useState(null); // slot name when open
  const [previewingSlot, setPreviewingSlot] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['bookshop-cms'],
    queryFn: async () => (await api.get('/bookshop/admin/content')).data,
  });
  const blocks = data?.blocks || [];

  const merged = React.useMemo(() => blocks.map((b) => ({ ...b, ...(dirtyBySlot[b.slot] || {}) })), [blocks, dirtyBySlot]);

  const set = (slot, field, value) => {
    setDirtyBySlot((prev) => ({ ...prev, [slot]: { ...(prev[slot] || {}), [field]: value } }));
  };

  const save = async (slot) => {
    const changes = dirtyBySlot[slot];
    if (!changes || Object.keys(changes).length === 0) { toast.info('Nothing to save'); return; }
    setSavingSlot(slot);
    try {
      await api.put(`/bookshop/admin/content/${slot}`, changes);
      toast.success(`${SLOT_LABELS[slot] || slot} saved`);
      setDirtyBySlot((prev) => { const c = { ...prev }; delete c[slot]; return c; });
      qc.invalidateQueries({ queryKey: ['bookshop-cms'] });
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSavingSlot(''); }
  };

  // Iter 423 enhancement: one-click preview link per block. Tokens are stored
  // hashed, so a fresh one is minted each time rather than reusing a listed one.
  const STOREFRONT_URL = 'https://faithworldministries.com/faith-works-bookshop/';
  const previewSlotInStorefront = async (slot) => {
    setPreviewingSlot(slot);
    try {
      const { data: created } = await api.post('/bookshop/admin/preview-tokens', { label: `block:${slot}`, ttl_hours: 24 });
      const token = created.token;
      qc.invalidateQueries({ queryKey: ['bookshop-preview-tokens'] });
      const sep = STOREFRONT_URL.includes('?') ? '&' : '?';
      const url = `${STOREFRONT_URL}${sep}fwb_preview=${encodeURIComponent(token)}#fwb-${slot}`;
      window.open(url, '_blank', 'noopener');
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setPreviewingSlot(''); }
  };

  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;

  return (
    <div className="space-y-3">
      <div className="border border-indigo-200 bg-indigo-50 rounded p-3 text-xs text-indigo-900">
        <p>Edit the copy, imagery and calls-to-action that show up on the public storefront. Toggle a block off to hide it (default policy blocks can{`'`}t be deleted — only hidden). Storefront picks these up via <code>{'<div data-fwb="content" data-slot="...">'}</code>.</p>
      </div>
      {merged.map((b) => {
        const dirty = !!dirtyBySlot[b.slot];
        return (
          <Card key={b.slot} data-testid={`content-block-${b.slot}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="h-4 w-4 text-indigo-600" />
                    {SLOT_LABELS[b.slot] || b.slot}
                    {!b.is_active && <Badge className="bg-slate-200 text-slate-600 text-[10px]">Hidden</Badge>}
                    {dirty && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Unsaved</Badge>}
                  </CardTitle>
                  <CardDescription className="text-xs">slot: <code>{b.slot}</code></CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 text-xs">
                    <Switch checked={!!b.is_active} onCheckedChange={(v) => set(b.slot, 'is_active', v)} data-testid={`toggle-active-${b.slot}`} />
                    Active
                  </label>
                  <Button size="sm" variant="outline" onClick={() => previewSlotInStorefront(b.slot)} disabled={previewingSlot === b.slot} data-testid={`preview-block-${b.slot}`} title="Open storefront jumped to this block">
                    {previewingSlot === b.slot ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ExternalLink className="h-3.5 w-3.5 mr-1" /> Preview</>}
                  </Button>
                  <Button size="sm" onClick={() => save(b.slot)} disabled={!dirty || savingSlot === b.slot} data-testid={`save-block-${b.slot}`}>
                    {savingSlot === b.slot ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Title</Label>
                  <Input value={b.title || ''} onChange={(e) => set(b.slot, 'title', e.target.value)} data-testid={`block-title-${b.slot}`} />
                </div>
                <div>
                  <Label className="text-xs">Subtitle</Label>
                  <Input value={b.subtitle || ''} onChange={(e) => set(b.slot, 'subtitle', e.target.value)} data-testid={`block-subtitle-${b.slot}`} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Body (markdown: **bold**, *em*, [link](url))</Label>
                <Textarea rows={3} value={b.body || ''} onChange={(e) => set(b.slot, 'body', e.target.value)} data-testid={`block-body-${b.slot}`} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div className="md:col-span-1">
                  <div className="flex items-start gap-1">
                    <MediaUpload value={b.image_url || ''} onChange={(url) => set(b.slot, 'image_url', url)} purpose="cover" label="Image" testId={`block-image-${b.slot}`} className="flex-1" previewClassName="h-16" />
                    <Button size="sm" variant="outline" onClick={() => setPickerFor(b.slot)} data-testid={`pick-image-${b.slot}`} title="Pick from media library">
                      <ImageIcon className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">CTA label</Label>
                  <Input value={b.cta_label || ''} onChange={(e) => set(b.slot, 'cta_label', e.target.value)} data-testid={`block-cta-label-${b.slot}`} />
                </div>
                <div>
                  <Label className="text-xs">CTA URL</Label>
                  <Input value={b.cta_url || ''} onChange={(e) => set(b.slot, 'cta_url', e.target.value)} placeholder="/shop or https://…" data-testid={`block-cta-url-${b.slot}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
      <MediaPickerDialog
        open={!!pickerFor}
        onClose={() => setPickerFor(null)}
        onPick={(url) => { if (pickerFor) set(pickerFor, 'image_url', url); setPickerFor(null); }}
      />
    </div>
  );
};

// ================== MEDIA LIBRARY ==================
const MediaLibrary = () => {
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');
  const [tags, setTags] = useState('');
  const [copiedId, setCopiedId] = useState('');
  const [q, setQ] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['bookshop-media', q],
    queryFn: async () => (await api.get('/bookshop/admin/media', { params: { q: q || undefined, limit: 200 } })).data,
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/bookshop/admin/media/${id}`),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['bookshop-media'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const backend = process.env.REACT_APP_BACKEND_URL;
  const toFullUrl = (u) => (u && u.startsWith('/') ? `${backend}${u}` : u);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        if (caption) fd.append('caption', caption);
        if (tags) fd.append('tags', tags);
        await api.post('/bookshop/admin/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(`Uploaded ${files.length} file(s)`);
      setCaption(''); setTags('');
      qc.invalidateQueries({ queryKey: ['bookshop-media'] });
    } catch (e) { toast.error(getErrorMessage(e, 'Upload failed')); }
    finally { setUploading(false); }
  };

  const media = data?.media || [];
  return (
    <div className="space-y-3">
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Upload className="h-4 w-4" /> Upload images</CardTitle>
          <CardDescription>PNG/JPG/GIF/WebP/SVG · max 10 MB per file. URLs are copy-paste ready for product covers or content blocks.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div><Label className="text-xs">Caption (optional)</Label><Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. 2026 Advent hero" data-testid="media-caption" /></div>
            <div><Label className="text-xs">Tags (comma-separated)</Label><Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="hero, banner" data-testid="media-tags" /></div>
          </div>
          <input type="file" accept="image/*" multiple onChange={(e) => handleUpload(e.target.files)} disabled={uploading} data-testid="media-upload-input" />
          {uploading && <p className="text-xs text-slate-500"><Loader2 className="h-3 w-3 inline mr-1 animate-spin" /> Uploading…</p>}
        </CardContent>
      </Card>
      <div className="flex items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search caption / filename" className="max-w-sm" data-testid="media-search" />
        <span className="text-xs text-slate-500">{media.length} of {data?.total || 0}</span>
      </div>
      {isLoading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : (
        media.length === 0 ? (
          <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-400">No media yet. Drag images above to get started.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {media.map((m) => {
              const full = toFullUrl(m.url);
              return (
                <Card key={m.id} data-testid={`media-item-${m.id}`}>
                  <CardContent className="p-2 space-y-2">
                    <div className="aspect-square bg-slate-100 rounded overflow-hidden flex items-center justify-center">
                      <img src={full} alt={m.alt_text || m.caption || m.original_filename} className="max-h-full max-w-full object-contain" />
                    </div>
                    <p className="text-xs truncate" title={m.caption || m.original_filename}>{m.caption || m.original_filename}</p>
                    <p className="text-[10px] text-slate-500">{Math.round((m.size_bytes || 0) / 1024)} KB · {(m.tags || []).join(', ') || '—'}</p>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => { navigator.clipboard.writeText(full); setCopiedId(m.id); setTimeout(() => setCopiedId(''), 1200); }} data-testid={`copy-media-${m.id}`}>
                        {copiedId === m.id ? 'Copied' : <><Copy className="h-3 w-3 mr-1" /> Copy URL</>}
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => { if (window.confirm('Delete this image?')) del.mutate(m.id); }} data-testid={`del-media-${m.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};

const MediaPickerDialog = ({ open, onClose, onPick }) => {
  const { data } = useQuery({
    queryKey: ['bookshop-media-picker'],
    queryFn: async () => (await api.get('/bookshop/admin/media', { params: { limit: 200 } })).data,
    enabled: open,
  });
  const backend = process.env.REACT_APP_BACKEND_URL;
  const toFullUrl = (u) => (u && u.startsWith('/') ? `${backend}${u}` : u);
  const media = data?.media || [];
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="media-picker">
        <DialogHeader><DialogTitle>Pick an image</DialogTitle>
          <DialogDescription>Click a thumbnail to insert its URL into the field.</DialogDescription>
        </DialogHeader>
        {media.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-10">Upload images from the Media tab first.</p>
        ) : (
          <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
            {media.map((m) => (
              <button key={m.id} type="button" onClick={() => onPick(toFullUrl(m.url))} className="border rounded p-1 hover:border-indigo-500 transition text-left" data-testid={`picker-item-${m.id}`}>
                <img src={toFullUrl(m.url)} alt={m.alt_text || ''} className="w-full h-24 object-contain bg-slate-50" />
                <p className="text-[10px] mt-1 truncate">{m.caption || m.original_filename}</p>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ================== PREVIEW TOKENS ==================
const PreviewTokens = () => {
  const qc = useQueryClient();
  const backend = process.env.REACT_APP_BACKEND_URL;
  const [label, setLabel] = useState('');
  const [ttl, setTtl] = useState(24);
  const [publicUrl, setPublicUrl] = useState('https://faithworldministries.com/faith-works-bookshop/');
  const [copiedId, setCopiedId] = useState('');
  // A raw preview token exists only in the create response — the server keeps
  // just its hash, so it can never be listed again.
  const [freshToken, setFreshToken] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['bookshop-preview-tokens'],
    queryFn: async () => (await api.get('/bookshop/admin/preview-tokens')).data,
  });

  const create = useMutation({
    mutationFn: () => api.post('/bookshop/admin/preview-tokens', { label: label || undefined, ttl_hours: Number(ttl) || 24 }),
    onSuccess: (res) => {
      toast.success('Preview link created');
      setLabel('');
      setFreshToken(res?.data?.token || '');
      qc.invalidateQueries({ queryKey: ['bookshop-preview-tokens'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const revoke = useMutation({
    mutationFn: (id) => api.delete(`/bookshop/admin/preview-tokens/${id}`),
    onSuccess: () => { toast.success('Revoked'); qc.invalidateQueries({ queryKey: ['bookshop-preview-tokens'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const buildPreviewUrl = (token) => {
    const base = (publicUrl || '').trim() || 'https://faithworldministries.com/faith-works-bookshop/';
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}fwb_preview=${encodeURIComponent(token)}`;
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedId(key);
    setTimeout(() => setCopiedId(''), 1500);
  };

  const tokens = data?.tokens || [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Eye className="h-4 w-4" /> Storefront preview links</CardTitle>
          <CardDescription>
            Create a shareable link so you (or reviewers) can see <b>draft products</b> on the live storefront before publishing.
            Draft cards show a purple <b>DRAFT PREVIEW</b> badge and can’t be added to cart. Everyone without the link still sees the published catalogue.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Label (optional)</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Advent 2026 batch" data-testid="preview-label" />
            </div>
            <div>
              <Label>Expires in (hours)</Label>
              <Input type="number" min="1" max="720" value={ttl} onChange={(e) => setTtl(e.target.value)} data-testid="preview-ttl" />
            </div>
            <div>
              <Label>Storefront URL</Label>
              <Input value={publicUrl} onChange={(e) => setPublicUrl(e.target.value)} data-testid="preview-public-url" />
            </div>
          </div>
          <Button onClick={() => create.mutate()} disabled={create.isPending} data-testid="create-preview-token-btn">
            {create.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Generate preview link
          </Button>
          {freshToken && (
            <div className="border border-amber-300 bg-amber-50 rounded p-3" data-testid="fresh-preview-link-card">
              <p className="text-xs text-amber-900">
                Copy this link now — only a hash of the token is stored, so it cannot be shown again.
                Generate a new link whenever you need one.
              </p>
              <pre className="text-[11px] bg-slate-900 text-emerald-300 p-2 rounded mt-2 overflow-x-auto" data-testid="fresh-preview-link">{buildPreviewUrl(freshToken)}</pre>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={() => copy(buildPreviewUrl(freshToken), 'fresh')} data-testid="copy-fresh-preview-link">
                  {copiedId === 'fresh' ? 'Copied' : <><Copy className="h-3.5 w-3.5 mr-1" /> Copy</>}
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.open(buildPreviewUrl(freshToken), '_blank')} data-testid="open-fresh-preview-link">
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setFreshToken('')} data-testid="dismiss-fresh-preview-link">Done</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Active preview links</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : (
            tokens.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No preview links yet.</p>
            ) : (
              <div className="space-y-2">
                {tokens.map((t) => {
                  return (
                    <div key={t.id} className={`border rounded p-3 ${t.active ? '' : 'opacity-60'}`} data-testid={`preview-token-row-${t.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium truncate">{t.label || <span className="text-slate-400 italic">Unlabelled</span>}</p>
                            {t.active
                              ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Active</Badge>
                              : <Badge className="bg-slate-200 text-slate-600 text-[10px]">{t.revoked ? 'Revoked' : 'Expired'}</Badge>}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Expires {t.expires_at ? new Date(t.expires_at).toLocaleString() : '—'} · Created {new Date(t.created_at).toLocaleString()}
                          </p>
                          <p className="text-xs text-slate-500 mt-1" data-testid={`preview-token-hint-${t.id}`}>
                            Token {t.token_hint || '—'} · the full link is only shown when it is created
                          </p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {t.active && (
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => { if (window.confirm('Revoke this preview link?')) revoke.mutate(t.id); }} data-testid={`revoke-preview-${t.id}`}>
                              <EyeOff className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </CardContent>
      </Card>

      <div className="border border-indigo-200 bg-indigo-50 rounded p-3 text-xs text-indigo-900">
        <p><b>How reviewers use it:</b> Open the preview URL in any browser. The embed script detects <code>?fwb_preview=&lt;token&gt;</code>,
          stores it locally, and shows a purple banner. Draft products are then visible until they hit <b>Exit preview</b> or the token expires/is revoked.
          The regular public URL <b>never</b> shows drafts.</p>
      </div>
    </div>
  );
};

// ================== EMBED INSTRUCTIONS ==================
const EmbedInstructions = () => {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const [copied, setCopied] = useState('');
  const copy = (text, key) => { navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(''), 1500); };
  const script = `<script src="${backend}/api/bookshop/public/embed.js" defer></script>`;
  const productsMarkup = `<!-- Featured books grid -->
<div data-fwb="products" data-featured="true" data-limit="8"></div>

<!-- CMS content blocks -->
<div data-fwb="content" data-slot="hero"></div>
<div data-fwb="content" data-slot="announcement_bar"></div>
<div data-fwb="content" data-slot="about_intro"></div>

<!-- Category chips -->
<div data-fwb="categories"></div>

<!-- Cart badge in header -->
<span data-fwb="cart-count">0</span>`;
  return (
    <Card><CardHeader><CardTitle className="text-base">Add the public storefront to your website</CardTitle>
      <CardDescription>Paste these snippets into <code>faithworldministries.com/faith-works-bookshop/index.html</code> (and shop.html, category.html). The embed script fetches live products from this backend.</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div>
        <Label className="text-xs">1) Add once, before <code>&lt;/body&gt;</code>:</Label>
        <div className="relative"><pre className="bg-slate-900 text-emerald-300 p-3 rounded text-xs overflow-x-auto" data-testid="embed-script">{script}</pre>
          <Button size="sm" variant="outline" className="absolute top-2 right-2" onClick={() => copy(script, 'script')} data-testid="copy-embed-script">{copied === 'script' ? 'Copied' : <><Copy className="h-3.5 w-3.5 mr-1" /> Copy</>}</Button>
        </div>
      </div>
      <div>
        <Label className="text-xs">2) Wire your existing HTML with these <code>data-fwb</code> attributes:</Label>
        <div className="relative"><pre className="bg-slate-900 text-emerald-300 p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap">{productsMarkup}</pre>
          <Button size="sm" variant="outline" className="absolute top-2 right-2" onClick={() => copy(productsMarkup, 'markup')}>{copied === 'markup' ? 'Copied' : <><Copy className="h-3.5 w-3.5 mr-1" /> Copy</>}</Button>
        </div>
      </div>
      <div className="border border-emerald-200 bg-emerald-50 rounded p-3 text-xs text-emerald-900">
        <p><b>Available embed slots:</b></p>
        <ul className="list-disc list-inside">
          <li>{'data-fwb="products" · attrs: data-limit, data-featured, data-bestseller, data-new-release, data-category="slug", data-q="search"'}</li>
          <li>{'data-fwb="categories" — renders category chips'}</li>
          <li>{'data-fwb="content" · attrs: data-slot="hero|announcement_bar|about_intro|about_page|contact_page|returns_page|privacy_page|terms_page|faq_page|shipping_page|digital_resources_page|conference_resources_page|authors_intro|newsletter_cta|policy_shipping|policy_returns|policy_privacy|footer_note" — renders CMS blocks'}</li>
          <li>{'data-fwb="authors" · attrs: data-featured=true, data-limit — author grid (auto-links to ?author=<slug>)'}</li>
          <li>{'data-fwb="author" · attrs: data-slug OR reads ?author= from URL — full author page with bio + books'}</li>
          <li>{'data-fwb="publishers" — publisher list'}</li>
          <li>{'data-fwb="discount" — cart discount code input (auto-validates against backend, stores applied code in localStorage)'}</li>
          <li>{'data-fwb="add-to-cart" · attrs: data-product-id — auto-wired'}</li>
          <li>{'data-fwb="cart-count" — live cart badge'}</li>
        </ul>
      </div>
      <div className="border border-slate-200 bg-slate-50 rounded p-3 text-xs text-slate-700">
        <p><b>API endpoints available at:</b> <a className="text-indigo-600 underline" href={`${backend}/api/bookshop/public/products`} target="_blank" rel="noreferrer"><code>{backend}/api/bookshop/public/products</code></a></p>
      </div>
    </CardContent></Card>
  );
};

export default BookshopAdmin;
